import { buildColorRule } from './build-color-rule.js';
import type {
  ColorPalette,
  CustomColorPalette,
} from '../../core/types/color-palette.js';
import { ColorRuleProps } from '../../core/types/color-rule.js';
import { getColorPalette } from '../../core/utils/get-color-palette.js';
import type {
  CategoricalLegendColorMap,
  InternalTreeMapNode,
  LegendColorMap,
} from '../types/internal.js';
import type { NameAccessor } from '../types/treemap.js';

/**
 * Function to map clusters or nodes to color tokens
 * @param rootNode - InternalTreeMapNode
 * @param colorPalette - color palette
 * @param nameAccessor - targeted property on the leaf node
 * @param colorRules - color rules to apply to the legend
 * @returns - LegendColorMap
 */
export const getCategoricalLegendColorMap = (
  rootNode: InternalTreeMapNode,
  colorPalette: ColorPalette | CustomColorPalette,
  nameAccessor: NameAccessor | undefined,
  colorRules: ColorRuleProps[],
) => {
  const legendColorMap: LegendColorMap = new Map() as CategoricalLegendColorMap;
  let colorIdx = 0;

  const legendTarget = nameAccessor ?? 'name';
  const legendColorRules = colorRules.filter(
    (rule) => !rule.valueAccessor || rule.valueAccessor === legendTarget,
  );

  rootNode.children?.forEach((cluster) => {
    if (nameAccessor) {
      cluster.children?.forEach((node) => {
        const labelKey = node[nameAccessor] as string;
        const ruleColor = buildColorRule(node, legendColorRules, nameAccessor);
        if (!legendColorMap.has(labelKey)) {
          const colorToken = ruleColor
            ? { Default: ruleColor }
            : getColorPalette(labelKey, colorIdx, colorPalette);
          legendColorMap.set(labelKey, { color: colorToken });
          colorIdx++;
        }
      });
    } else {
      const labelKey = cluster.name;
      const ruleColor = buildColorRule(cluster, legendColorRules);
      if (!legendColorMap.has(labelKey)) {
        const colorToken = ruleColor
          ? { Default: ruleColor }
          : getColorPalette(labelKey, colorIdx, colorPalette);
        legendColorMap.set(labelKey, {
          color: colorToken,
          value: cluster.value,
        });
        colorIdx++;
      }
    }
  });

  return legendColorMap;
};
